import { z } from "zod";

/**
 * Unsold consumer-goods destruction (draft TR ESPR Art. 23–26 + EK-7, mirroring
 * ESPR (EU) 2024/1781 Art. 24–25). Two obligations:
 *  - the destruction BAN for apparel, clothing accessories and footwear — large
 *    enterprises from 19 July 2026, medium enterprises from 19 July 2030,
 *    micro/small exempt (Art. 25 + Art. 74);
 *  - the annual public DISCLOSURE of discarded unsold goods (Art. 24): count and
 *    weight per product type, reasons, exemption relied on, share sent to each
 *    treatment, and the measures taken — on an easily accessible page of the
 *    operator's own website.
 */

export const ENTERPRISE_SIZES = ["micro", "small", "medium", "large"] as const;
export type EnterpriseSize = (typeof ENTERPRISE_SIZES)[number];
export const enterpriseSizeSchema = z.enum(ENTERPRISE_SIZES);
export const setEnterpriseSizeSchema = z.object({ enterpriseSize: enterpriseSizeSchema.nullable() });
export type SetEnterpriseSizeInput = z.infer<typeof setEnterpriseSizeSchema>;

/** Product types under the ban (EK-7); "other" covers voluntary disclosure of anything else. */
export const DISPOSAL_CATEGORIES = ["apparel", "clothing_accessories", "footwear", "other"] as const;
export type DisposalCategory = (typeof DISPOSAL_CATEGORIES)[number];

/** Why the goods were discarded — the Art. 25(5) exemption grounds plus "other". */
export const DISPOSAL_REASONS = [
  "health_safety",
  "damaged_unrepairable",
  "unfit_for_purpose",
  "donation_refused",
  "not_reusable",
  "ip_infringement",
  "least_impact_option",
  "other",
] as const;
export type DisposalReason = (typeof DISPOSAL_REASONS)[number];

/** Ban application dates (Art. 74): large 19/7/2026, medium 19/7/2030; micro & small exempt. */
export const DESTRUCTION_BAN_FROM: Readonly<Partial<Record<EnterpriseSize, string>>> = {
  large: "2026-07-19",
  medium: "2030-07-19",
};

export interface DisposalApplicability {
  /** The ban (and with it the disclosure duty) applies to this size at `asOf`. */
  applies: boolean;
  /** When it applies / will apply; null for exempt sizes or an unknown size. */
  from: string | null;
  /** Size is unknown — the org has not declared it yet. */
  unknown: boolean;
}

export function disposalApplicability(
  size: EnterpriseSize | null | undefined,
  asOf: Date,
): DisposalApplicability {
  if (!size) return { applies: false, from: null, unknown: true };
  const from = DESTRUCTION_BAN_FROM[size] ?? null;
  if (!from) return { applies: false, from: null, unknown: false };
  return { applies: asOf.getTime() >= Date.parse(`${from}T00:00:00.000Z`), from, unknown: false };
}

const pct = z.number().min(0).max(100);

export const disposalLineSchema = z
  .object({
    productCategory: z.enum(DISPOSAL_CATEGORIES),
    categoryNote: z.string().trim().max(200).optional(),
    unitCount: z.number().int().min(0).max(1_000_000_000),
    weightKg: z.number().min(0).max(1_000_000_000),
    reason: z.enum(DISPOSAL_REASONS),
    reasonNote: z.string().trim().max(500).optional(),
    /** Exemption relied on (Art. 25(5)), when the destruction ban applies. */
    exemptionRef: z.string().trim().max(200).optional(),
    preparedForReusePct: pct,
    recycledPct: pct,
    otherRecoveryPct: pct,
    disposedPct: pct,
    measuresNote: z.string().trim().max(2000).optional(),
  })
  .refine(
    (l) => Math.abs(l.preparedForReusePct + l.recycledPct + l.otherRecoveryPct + l.disposedPct - 100) < 0.01,
    { message: "Treatment shares must add up to 100 %", path: ["disposedPct"] },
  );
export type DisposalLineInput = z.infer<typeof disposalLineSchema>;

export const disposalReportBodySchema = z.object({
  note: z.string().trim().max(2000).optional(),
  lines: z.array(disposalLineSchema).max(50),
});
/** One line as the client sends it (before defaults/transforms). */
export type DisposalLineRequest = z.input<typeof disposalLineSchema>;
export type DisposalReportInput = z.infer<typeof disposalReportBodySchema>;

export const disposalYearParamSchema = z.object({
  fiscalYear: z.coerce.number().int().min(2020).max(2100),
});
